/**
 * Weather and time of day for a shift. They come from the shift's random seed, so the daily challenge and
 * friend challenges (which share a seed) get the same conditions for everyone. Shared so the server's fare
 * limit knows the most a shift can pay.
 */
export type Weather = 'clear' | 'rain' | 'harmattan';
export type TimeOfDay = 'day' | 'dusk' | 'night';

export interface Conditions {
  weather: Weather;
  time: TimeOfDay;
}

export const CLEAR_DAY: Conditions = { weather: 'clear', time: 'day' };

const WEATHER_ODDS: readonly [Weather, number][] = [['clear', 0.6], ['rain', 0.2], ['harmattan', 0.2]];
const TIME_ODDS: readonly [TimeOfDay, number][] = [['day', 0.55], ['dusk', 0.15], ['night', 0.3]];

/** A tiny stable hash of two numbers to 0..1 (not for security). */
function unit(seed: number, salt: number): number {
  let h = (seed ^ Math.imul(salt, 0x9e3779b1)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 0x1_0000_0000;
}

function pick<T>(odds: readonly [T, number][], roll: number): T {
  let left = roll;
  for (const [value, share] of odds) {
    left -= share;
    if (left < 0) return value;
  }
  return odds[odds.length - 1]![0];
}

export function conditionsFor(seed: number): Conditions {
  return { weather: pick(WEATHER_ODDS, unit(seed, 11)), time: pick(TIME_ODDS, unit(seed, 29)) };
}

/** What the conditions do to the game. 1 = no change. */
export interface ConditionEffects {
  /** Tyre grip: lower means slower lane changes and longer braking. */
  grip: number;
  /** Pulling-away strength. */
  accel: number;
  /** Other cars drive at this fraction of their speed. */
  trafficSpeed: number;
  /** Multiplies every fare. */
  fare: number;
  /** Multiplies how many people wait at stops. */
  crowd: number;
  /** Multiplies the perfect-stop tip. */
  tip: number;
}

const WEATHER_EFFECTS: Record<Weather, ConditionEffects> = {
  clear: { grip: 1, accel: 1, trafficSpeed: 1, fare: 1, crowd: 1, tip: 1 },
  rain: { grip: 0.7, accel: 0.9, trafficSpeed: 0.85, fare: 1.25, crowd: 1, tip: 1 },
  harmattan: { grip: 1, accel: 1, trafficSpeed: 1, fare: 1.15, crowd: 1, tip: 1 },
};

const TIME_EFFECTS: Record<TimeOfDay, ConditionEffects> = {
  day: { grip: 1, accel: 1, trafficSpeed: 1, fare: 1, crowd: 1, tip: 1 },
  dusk: { grip: 1, accel: 1, trafficSpeed: 1, fare: 1.05, crowd: 0.9, tip: 1.25 },
  night: { grip: 1, accel: 1, trafficSpeed: 1, fare: 1.1, crowd: 0.6, tip: 2 },
};

export function effectsOf(conditions: Conditions): ConditionEffects {
  const w = WEATHER_EFFECTS[conditions.weather];
  const t = TIME_EFFECTS[conditions.time];
  return { grip: w.grip * t.grip, accel: w.accel * t.accel, trafficSpeed: w.trafficSpeed * t.trafficSpeed, fare: w.fare * t.fare, crowd: w.crowd * t.crowd, tip: w.tip * t.tip };
}

/** The most the conditions can raise a fare and a tip (rain + night): the server's limits allow for it. */
export const MAX_CONDITION_FARE = Math.max(...Object.values(WEATHER_EFFECTS).map((e) => e.fare)) * Math.max(...Object.values(TIME_EFFECTS).map((e) => e.fare));
export const MAX_CONDITION_TIP = Math.max(...Object.values(TIME_EFFECTS).map((e) => e.tip));

const WEATHER_TEXT: Record<Weather, { icon: string; name: string }> = {
  clear: { icon: '☀️', name: 'Clear' },
  rain: { icon: '🌧️', name: 'Rain' },
  harmattan: { icon: '🌫️', name: 'Harmattan haze' },
};
const TIME_TEXT: Record<TimeOfDay, { icon: string; name: string }> = {
  day: { icon: '☀️', name: 'Day' },
  dusk: { icon: '🌇', name: 'Dusk' },
  night: { icon: '🌙', name: 'Night' },
};

const percent = (factor: number): string => `${factor >= 1 ? '+' : '−'}${Math.round(Math.abs(factor - 1) * 100)}%`;

/** Words for the HUD, the shift-start toast and the menu. `null` when it's a plain clear day. */
export function describeConditions(conditions: Conditions): { chip: string; headline: string; detail: string } | null {
  if (conditions.weather === 'clear' && conditions.time === 'day') return null;
  const parts: { icon: string; name: string }[] = [];
  if (conditions.weather !== 'clear') parts.push(WEATHER_TEXT[conditions.weather]);
  if (conditions.time !== 'day') parts.push(TIME_TEXT[conditions.time]);
  const effects = effectsOf(conditions);
  const notes: string[] = [];
  if (effects.grip < 1) notes.push('slippery roads');
  if (conditions.weather === 'harmattan') notes.push('hazy, hard to see far');
  if (conditions.time === 'night') notes.push('fewer riders, bigger tips');
  if (effects.fare !== 1) notes.push(`fares ${percent(effects.fare)}`);
  return {
    chip: parts.map((p) => `${p.icon} ${p.name}`).join(' · '),
    headline: parts.map((p) => p.name.toUpperCase()).join(' + ') + '!',
    detail: notes.join(' · '),
  };
}
